import { SuggestionMenu } from "@circle-react-shared/SuggestionMenu";
import type { SuggestionItem } from "@circle-react-shared/SuggestionMenu/types";
import type { SuggestionListHandle } from "@circle-react-shared/SuggestionMenu/useSuggestionListKeyboard";
import { useSuggestionListKeyboard } from "@circle-react-shared/SuggestionMenu/useSuggestionListKeyboard";
import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import { useTipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { useBlockEditorContext } from "@circle-react-uikit/TipTapBlockEditor/BlockEditorContext";
import { Avatar } from "@circleco/compass/components/Avatar";
import { Icon } from "@circleco/compass/components/Icon";
import type { Editor } from "@tiptap/core";
import { isArray, uniqBy } from "lodash";
import { forwardRef, useEffect, useMemo } from "react";

import { getMentionScope } from "./helper";
import { useMentions } from "./hooks/useMentions";

interface MentionMember {
  avatar_url?: string;
  id: number | string;
  name: string;
  sgid: string;
  type?: string;
}

export interface CompassMentionListProps {
  clientRect?: (() => DOMRect | null) | null;
  command: (item: MentionMember) => void;
  editor: Editor;
  query: string;
}

export const CompassMentionList = forwardRef<
  SuggestionListHandle,
  CompassMentionListProps
>(({ clientRect, command, editor, query }, ref) => {
  const tiptapEditorContext = useTipTapEditorContext();
  const tiptapBlockEditorContext = useBlockEditorContext();
  const {
    addToLocalSgidToObjectMap,
    spaceId,
    chatProps,
    type: editorType,
    isUpdate,
  } = tiptapEditorContext || tiptapBlockEditorContext;
  const { isThread, isDirectMessaging, disableGroupMentions } = chatProps || {};
  const { setPopupsVisible } = useInternalEditorState();

  const { items, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useMentions({
      query,
      spaceId,
      scope: getMentionScope({
        spaceId,
        isDirectMessaging,
        isThread,
        editorType,
        isUpdate,
        disableGroupMentions,
      }),
    });

  const members: MentionMember[] = uniqBy(isArray(items) ? items : [], member =>
    String(member.id),
  );

  const { isOpen, menuRef } = useSuggestionListKeyboard({
    ref,
    query,
    isEnabled: members.length > 0 && !isLoading,
  });

  useEffect(() => {
    setPopupsVisible(isOpen);

    return () => setPopupsVisible(false);
  }, [isOpen, setPopupsVisible]);

  const groups = useMemo(() => {
    if (members.length === 0) return [];

    return [
      {
        id: "members",
        items: members.map(member => ({
          id: String(member.id),
          label: member.name,
          visual:
            member.type === "group_mention" ? (
              <Icon name="group" size="sm" />
            ) : (
              <Avatar size="xs" src={member.avatar_url} name={member.name} />
            ),
          data: member,
        })),
      },
    ];
  }, [members]);

  const onSelect = (item: SuggestionItem) => {
    const member = item.data as MentionMember;
    addToLocalSgidToObjectMap({ sgid: member.sgid, object: member });
    command(member);
    setPopupsVisible(false);
  };

  return (
    <SuggestionMenu
      ref={menuRef}
      resetKey={query}
      open={isOpen}
      groups={groups}
      hasMore={hasNextPage}
      getAnchorRect={() => clientRect?.() ?? null}
      anchorContextElement={editor?.view.dom}
      onSelect={onSelect}
      onEndReached={() => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
      }}
      side={tiptapEditorContext ? "top" : "bottom"}
    />
  );
});

CompassMentionList.displayName = "CompassMentionList";
